import React from 'react';

import { db } from 'Compass/profile-lab/services/db';
import { fetchDataset } from 'Compass/profile-lab/services/ProfileLabService';
import { AVAILABLE_MODELS } from 'Compass/profile-lab/utils/llmConstants';

export interface Message {
  content: string;
  sender: 'user' | 'assistant' | 'system';
  timestamp: number;
}

interface CommandContext {
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  db: typeof db;
}

interface Command {
  name: string;
  description: string;
  order?: string;
  execute: (args: string[], fullMessage: string, context: CommandContext) => Promise<void>;
}

const commands = new Map<string, Command>();

const registerCommand = (command: Command): void => {
  commands.set(command.name, command);
};

export const getCommand = (name: string): Command | undefined => {
  return commands.get(name);
};

const getAllCommands = (): Command[] => {
  return Array.from(commands.values());
};

const addMessage = async (message: Message, context: CommandContext): Promise<void> => {
  context.setMessages(prev => [...prev, message]);
  await context.db.addUIMessage(message);
};

const sysMessage = async (content: string, context: CommandContext): Promise<void> => {
  const systemMsg: Message = {
    content,
    sender: 'system',
    timestamp: Date.now()
  };
  await addMessage(systemMsg, context);
};

registerCommand({
  name: '/help',
  description: 'Show this help',
  order: '00',
  execute: async (_args, _fullMessage, context) => {
    const allCommands = getAllCommands();
    const sortedCommands = allCommands.sort((a, b) => {
      const orderA = (a.order || 'zz') + a.name;
      const orderB = (b.order || 'zz') + b.name;
      return orderA.localeCompare(orderB);
    });
    const commandList = sortedCommands
      .map(cmd => `${cmd.name} - ${cmd.description}`)
      .join('\n');
    await sysMessage(`Available commands:\n${commandList}`, context);
  }
});

registerCommand({
  name: '/reset',
  description: 'Clear chat history (for LLM context)',
  order: '41',
  execute: async (_args, _fullMessage, context) => {
    await context.db.clearChatHistory();
    await sysMessage('Chat history has been reset.', context);
  }
});

registerCommand({
  name: '/clear',
  description: 'Clear chat window',
  order: '42',
  execute: async (_args, _fullMessage, context) => {
    await context.db.clearUIMessages();
    context.setMessages([]);
  }
});

registerCommand({
  name: '/history',
  description: 'Toggle history transmission to LLM',
  order: '40',
  execute: async (_args, _fullMessage, context) => {
    const config = await context.db.getChatConfig();
    const newState = !config.sendHistory;
    await context.db.updateChatConfig({ sendHistory: newState });
    const statusText = newState ? 'enabled' : 'disabled';
    await sysMessage(`History transmission ${statusText}.`, context);
  }
});

registerCommand({
  name: '/model',
  description: 'Change chat model',
  order: '10',
  execute: async (args, _fullMessage, context) => {
    if (args.length < 1) {
      const config = await context.db.getChatConfig();
      const modelsList = AVAILABLE_MODELS.join(', ');
      await sysMessage(`Current model: ${config.modelType}\n\nAvailable models:\n${modelsList}`, context);
      return;
    }

    const modelName = args[0];
    if (!AVAILABLE_MODELS.includes(modelName)) {
      const modelsList = AVAILABLE_MODELS.join(', ');
      await sysMessage(`Invalid model: ${modelName}\n\nAvailable models:\n${modelsList}`, context);
      return;
    }

    await context.db.updateChatConfig({ modelType: modelName });
    await context.db.clearChatHistory();
    await sysMessage(`Model changed to ${modelName}. Chat history has been reset.`, context);
  }
});


registerCommand({
  name: '/status',
  description: 'Show current status',
  order: '00',
  execute: async (_args, _fullMessage, context) => {
    const config = await context.db.getChatConfig();
    const selectionState = await context.db.getSelectionState();
    const datasetState = await context.db.getDatasetState();
    const historyStatus = config.sendHistory ? 'enabled' : 'disabled';
    const activeDatasets = datasetState.datasets.filter(d => d.enabled);
    const datasetsText = activeDatasets.length > 0 ? activeDatasets.map(d => d.name).join(', ') : 'none';
    await sysMessage(`Model: ${config.modelType}\nHistory: ${historyStatus}\nDatasets: ${datasetsText}\nSelected: ${selectionState.selected.length}\nGrow: ${selectionState.grow.length}`, context);
  }
});


registerCommand({
  name: '/capture',
  description: 'Capture previous LLM response as a dataset',
  order: '32',
  execute: async (args, _fullMessage, context) => {
    if (args.length < 1 || !args[0]) {
      await sysMessage('Usage: /capture <name> - Capture the previous LLM response as a dataset', context);
      return;
    }

    const datasetName = args[0];
    const chatHistory = await context.db.getChatHistory();
    const lastAssistantMessage = [...chatHistory].reverse().find(msg => msg.sender === 'assistant');

    if (!lastAssistantMessage) {
      await sysMessage('No previous assistant response found to capture.', context);
      return;
    }

    await context.db.setDataset({
      name: datasetName,
      enabled: true,
      data: lastAssistantMessage.content,
      loadedAt: Date.now()
    });

    await sysMessage(`Captured previous response as dataset '${datasetName}' and activated it.`, context);
  }
});

registerCommand({
  name: '/set',
  description: 'Create a dataset',
  order: '33',
  execute: async (_args, fullMessage, context) => {
    const match = fullMessage.match(/^\/set\s+(\S+)\s+([\s\S]+)$/);

    if (!match) {
      await sysMessage('Usage: /set <name> <value> - Create a dataset', context);
      return;
    }

    const name = match[1];
    const value = match[2].trimStart();

    await context.db.setDataset({
      name: name,
      enabled: true,
      data: value,
      loadedAt: Date.now()
    });

    await sysMessage(`Created dataset '${name}' and activated it.`, context);
  }
});

registerCommand({
  name: '/show',
  description: 'Show a dataset',
  order: '34',
  execute: async (args, _fullMessage, context) => {
    if (args.length < 1 || !args[0]) {
      await sysMessage('Usage: /show <name> - Show a dataset', context);
      return;
    }

    const datasetName = args[0];
    const dataset = await context.db.getDatasetByName(datasetName);

    if (!dataset || !dataset.data) {
      await sysMessage(`No dataset found with name '${datasetName}'.`, context);
      return;
    }

    let displayData = dataset.data;
    if (datasetName === 'visits') {
      const lines = dataset.data.split('\n');
      if (lines.length > 5) {
        displayData = lines.slice(0, 5).join('\n') + '\n...';
      }
    }

    await sysMessage(`Dataset '${datasetName}':\n\n${displayData}`, context);
  }
});

registerCommand({
  name: '/remove',
  description: 'Remove a dataset',
  order: '35',
  execute: async (args, _fullMessage, context) => {
    if (args.length < 1 || !args[0]) {
      await sysMessage('Usage: /remove <name> - Remove a dataset', context);
      return;
    }

    const datasetName = args[0];
    const removed = await context.db.removeDataset(datasetName);

    if (!removed) {
      await sysMessage(`No dataset found with name '${datasetName}'.`, context);
      return;
    }

    await sysMessage(`Removed dataset '${datasetName}'.`, context);
  }
});


const handleDatasetCommand = async (args: string[], _fullMessage: string, context: CommandContext): Promise<void> => {
  const datasetName = args.length > 0 ? args[0] : '';
  const datasetState = await context.db.getDatasetState();

  if (!datasetName) {
    const statusLines = datasetState.datasets.map(d => {
      const status = d.enabled ? 'enabled' : 'disabled';
      let loadedInfo = '';
      if (d.loadedAt) {
        const dateStr = new Date(d.loadedAt).toLocaleString();
        if (d.data) {
          const lines = d.data.split('\n').length;
          const kb = (d.data.length / 1024).toFixed(1);
          loadedInfo = ` (loaded ${dateStr}, ${lines} lines, ${kb} kB)`;
        } else {
          loadedInfo = ` (loaded ${dateStr})`;
        }
      }
      return `${d.name}: ${status}${loadedInfo}`;
    });
    await sysMessage(`Dataset status:\n${statusLines.join('\n')}`, context);
    return;
  }

  const dataset = datasetState.datasets.find(d => d.name === datasetName);
  if (!dataset) {
    const availableNames = datasetState.datasets.map(d => d.name).join(', ');
    await sysMessage(`Unknown dataset: ${datasetName}. Available datasets: ${availableNames}`, context);
    return;
  }

  if (dataset.enabled) {
    await context.db.setDataset({ ...dataset, enabled: false });
    await sysMessage(`Dataset '${datasetName}' disabled.`, context);
    return;
  }

  if (dataset.data) {
    await context.db.setDataset({ ...dataset, enabled: true });
    const lines = dataset.data.split('\n').length;
    const kb = (dataset.data.length / 1024).toFixed(1);
    await sysMessage(`Dataset '${datasetName}' enabled (${lines} lines, ${kb} kB).`, context);
    return;
  }

  try {
    await sysMessage(`Loading dataset '${datasetName}'...`, context);

    const subjectConfig = await context.db.getSubjectConfig();
    const userLid = parseInt(subjectConfig.userLid, 10);

    if (!userLid) {
      await sysMessage('No user selected. Please select a user first.', context);
      return;
    }

    const data = await fetchDataset(userLid, datasetName);

    await context.db.setDataset({
      name: datasetName,
      enabled: true,
      data,
      loadedAt: Date.now()
    });

    const lines = data.split('\n').length;
    const kb = (data.length / 1024).toFixed(1);

    await sysMessage(`Dataset '${datasetName}' loaded and enabled (${lines} lines, ${kb} kB).`, context);
  } catch (error) {
    await sysMessage(`Failed to load dataset '${datasetName}': ${error}`, context);
  }
};

registerCommand({
  name: '/dataset',
  description: 'Show dataset status or toggle dataset inclusion',
  order: '30',
  execute: handleDatasetCommand
});

registerCommand({
  name: '/data',
  description: 'Show dataset status or toggle dataset inclusion',
  order: '31',
  execute: handleDatasetCommand
});


registerCommand({
  name: '/mode',
  description: 'Switch between adhoc and stage modes',
  order: '23',
  execute: async (args, _fullMessage, context) => {
    if (args.length < 1 || !args[0]) {
      const config = await context.db.getChatConfig();
      await sysMessage(`Current mode: ${config.mode}\n\nUsage: /mode <adhoc|stage>`, context);
      return;
    }

    const mode = args[0];
    if (mode !== 'adhoc' && mode !== 'stage') {
      await sysMessage('Invalid mode. Use /mode adhoc or /mode stage', context);
      return;
    }

    const workingStage = await context.db.getWorkingStage();
    if (mode === 'stage' && !workingStage) {
      await sysMessage('No stage loaded. Use /work <stage_id> to load a stage first.', context);
      return;
    }

    await context.db.updateChatConfig({ mode: mode as 'adhoc' | 'stage' });
    await sysMessage(`Switched to ${mode} mode.`, context);
  }
});

registerCommand({
  name: '/stop',
  description: 'Exit stage mode and clear working stage',
  order: '24',
  execute: async (_args, _fullMessage, context) => {
    const workingStage = await context.db.getWorkingStage();
    if (!workingStage) {
      await sysMessage('No stage is currently loaded.', context);
      return;
    }

    await context.db.clearWorkingStage();
    await context.db.updateChatConfig({ mode: 'adhoc' });
    await sysMessage(`Exited stage mode. Stage '${workingStage.stageId}' unloaded. Switched to adhoc mode.`, context);
  }
});
